// Relative imports (not `@/`) so bun can test this module.

import { getHexTileMapKey } from '../hexmap/model/keys';
import type { RuleSet } from '../rules/schema';
import {
  type Building,
  buildingLevel,
  type Command,
  canUpgrade,
  holdsStock,
  maxLevel,
  type PlayerId,
  type SimContext,
  type SimState,
  territoryOwners,
  type Unit,
  type UnitType,
  unitRank,
} from '../sim';
import { cityName } from './cities';
import type { Names } from './names';
import {
  affordable,
  cityPromoteCommand,
  cityPromotions,
  type PromotionOption,
  promoteCommands,
  sumCosts,
  type TrainOption,
  trainCommands,
  trainOption,
  typePromotions,
  typeTrainOption,
  unitName,
} from './promotions';
import { stockInUnits } from './resources';
import { levelPreview, type UpgradePreview, unitUpgradePreview } from './upgradePreview';

/**
 * Every upgrade the player owns as one list (pure, no React): city and
 * building levels (identical buildings of a city stack), unit-type upgrades
 * and rank-ups, each with the shared `UpgradePreview` the card draws, the
 * commands its button sends (the same ones the City and Units panels send)
 * and the ids to point at on the map. Reads the viewer's own state only, so
 * nothing about a rival can show.
 */

export type UpgradeKind = 'city' | 'building' | 'unit' | 'rank';

export interface UpgradeEntry {
  /** Stable across ticks, for selection. */
  id: string;
  kind: UpgradeKind;
  /** "City 1 → Town", "Farm ×2 · City 1 → Level 2", "Workers → Labourer". */
  title: string;
  /** One line of the effect: unlocks, else the first stat changes. */
  headline: string;
  /** Units (or stacked buildings) it applies to now, of `of` that qualify. */
  ready: number;
  of: number;
  /** The city it belongs to (a city's own level: itself); null for player-wide unit upgrades. */
  cityId: string | null;
  /** Whether the button can go ahead (`ready > 0`). */
  canDo: boolean;
  preview: UpgradePreview;
  /** What the button sends. */
  commands: Command[];
  /** Everything to ring on the map: the buildings and the own units it touches. */
  highlight: string[];
  /** Own buildings of the entry already upgrading to the level (shown as a timer, not a refusal). */
  upgrading: string[];
}

export const KIND_ORDER: readonly UpgradeKind[] = ['city', 'building', 'unit', 'rank'];

const KIND_LABEL: Record<UpgradeKind, string> = {
  city: 'Cities',
  building: 'Buildings',
  unit: 'Units',
  rank: 'Ranks',
};

export const kindLabel = (kind: UpgradeKind) => KIND_LABEL[kind];

/** One line for a row: unlocks first, else up to two stat changes. */
export function headlineOf(preview: UpgradePreview): string {
  if (preview.enables.length > 0) return `Unlocks ${preview.enables.slice(0, 3).join(', ')}`;
  const rows = preview.rows.slice(0, 2).map((r) => `${r.label} ${r.before} → ${r.after}`);
  return rows.length > 0 ? rows.join(' · ') : 'No stat changes';
}

function affectedIds(preview: UpgradePreview): string[] {
  return [...new Set(preview.affects.flatMap((g) => g.ids))];
}

// ── Builders ─────────────────────────────────────────────────────────────

/** Levels: one entry per (city, type, level) of completed own buildings below max level. */
function levelEntries(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<string, number>,
  names?: Names
): UpgradeEntry[] {
  const { rules } = ctx;
  const owners = territoryOwners(state, rules);
  const groups = new Map<string, { city: Building | undefined; members: Building[] }>();
  for (const b of Object.values(state.buildings)) {
    const def = rules.buildings[b.type];
    if (b.owner !== playerId || b.buildProgress < 1 || !def?.levels) continue;
    if (buildingLevel(b) >= maxLevel(def)) continue;
    const city = holdsStock(rules, b)
      ? b
      : state.buildings[owners.get(getHexTileMapKey(b.pos)) ?? ''];
    const key = `${city?.id ?? '-'}:${b.type}:${buildingLevel(b)}`;
    const group = groups.get(key) ?? { city, members: [] };
    group.members.push(b);
    groups.set(key, group);
  }
  const stock = stockInUnits(state, playerId);
  const entries: UpgradeEntry[] = [];
  for (const [key, { city, members }] of groups) {
    // Each member judged by the sim, then against what the ones before it spend.
    const left = { ...stock };
    const ready: Building[] = [];
    for (const b of members) {
      if (!canUpgrade(state, ctx, playerId, b.id, owners).ok) continue;
      const cost = levelPreview(state, ctx, playerId, b, cityNumbers, names)?.cost ?? {};
      if (!affordable(left, cost)) continue;
      for (const [res, n] of Object.entries(cost)) left[res] = (left[res] ?? 0) - n;
      ready.push(b);
    }
    const rep = ready[0] ?? members[0];
    const base = levelPreview(state, ctx, playerId, rep, cityNumbers, names);
    if (!base) continue;
    const isCity = base.kind === 'city';
    const preview: UpgradePreview = {
      ...base,
      cost: ready.length > 1 ? sumCosts(ready.map(() => base.cost)) : base.cost,
      count: ready.length,
      ok: ready.length > 0,
      reason: ready.length > 0 ? null : base.reason,
    };
    const where = city ? cityName(city, playerId, cityNumbers, undefined, names) : '';
    const name = rules.buildings[rep.type]?.name ?? rep.type;
    const stack = members.length > 1 ? ` ×${members.length}` : '';
    entries.push({
      id: `level:${key}`,
      kind: isCity ? 'city' : 'building',
      title: isCity
        ? `${where} → ${base.to.name}`
        : `${name}${stack}${where ? ` · ${where}` : ''} → ${base.to.name}`,
      headline: headlineOf(base),
      ready: ready.length,
      of: members.length,
      cityId: city?.id ?? null,
      canDo: ready.length > 0,
      preview,
      commands: ready.map((b) => ({ type: 'upgrade', buildingId: b.id })),
      highlight: [...members.map((b) => b.id), ...affectedIds(base)],
      upgrading: members.filter((b) => b.upgrading).map((b) => b.id),
    });
  }
  return entries;
}

function promotionEntry(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  real: SimState,
  cityNumbers: ReadonlyMap<string, number>,
  from: UnitType,
  option: PromotionOption,
  cityId: string | null,
  commands: Command[],
  names?: Names
): UpgradeEntry | null {
  if (option.eligible.length === 0) return null;
  const { rules } = ctx;
  const preview = unitUpgradePreview(state, ctx, playerId, from, option, real, cityNumbers, names);
  return {
    id: `unit:${cityId ?? '*'}:${from}>${option.to}`,
    kind: 'unit',
    title: `${unitName(rules, from, 2)} → ${unitName(rules, option.to, 1)}`,
    headline: headlineOf(preview),
    ready: option.ready.length,
    of: option.eligible.length,
    cityId,
    canDo: option.ready.length > 0,
    preview,
    commands,
    highlight: affectedIds(preview),
    upgrading: [],
  };
}

/** The rank-up card: hp and damage per rank, the cost, why units are held back. */
export function rankPreview(rules: RuleSet, type: UnitType, option: TrainOption): UpgradePreview {
  const { hpPerRank, strengthPerRank } = rules.veterancy;
  const rank = unitRank(rules, option.ready[0] ?? option.eligible[0]);
  const pct = (n: number) => `+${Math.round(n * 100)}%`;
  const rows = [
    ['hp', 'Max hp', hpPerRank],
    ['strength', 'Damage', strengthPerRank],
  ].map(([key, label, per]) => ({
    key: key as string,
    label: label as string,
    before: pct(rank * (per as number)),
    after: pct((rank + 1) * (per as number)),
    delta: pct(per as number),
    trend: 'up' as const,
  }));
  const readyIds = new Set(option.ready.map((u) => u.id));
  const held = option.eligible.filter((u) => !readyIds.has(u.id));
  const affects = [];
  if (option.ready.length > 0) {
    affects.push({ label: `${option.ready.length} ready`, ids: option.ready.map((u) => u.id) });
  }
  if (held.length > 0) {
    affects.push({
      label: `${held.length} held back`,
      ids: held.map((u) => u.id),
      held: true,
    });
  }
  const ok = option.ready.length > 0;
  return {
    kind: 'rank',
    verb: 'rankUp',
    from: { name: unitName(rules, type, 2), tag: `rank ${rank}` },
    to: { name: unitName(rules, type, 2), tag: `rank ${rank + 1}` },
    rows,
    requirements: [],
    enables: [],
    affects,
    breakdown: null,
    cost: option.cost,
    count: option.ready.length,
    ok,
    reason: ok ? null : (option.reason ?? 'Not now'),
  };
}

function rankEntry(
  rules: RuleSet,
  type: UnitType,
  option: TrainOption,
  cityId: string | null
): UpgradeEntry {
  const preview = rankPreview(rules, type, option);
  const { hpPerRank, strengthPerRank } = rules.veterancy;
  return {
    id: `rank:${cityId ?? '*'}:${type}`,
    kind: 'rank',
    title: `${unitName(rules, type, 2)} → next rank`,
    headline:
      hpPerRank === strengthPerRank
        ? `+${Math.round(hpPerRank * 100)}% hp and damage per rank`
        : headlineOf(preview),
    ready: option.ready.length,
    of: option.eligible.length,
    cityId,
    canDo: option.ready.length > 0,
    preview,
    commands: trainCommands(option),
    highlight: affectedIds(preview),
    upgrading: [],
  };
}

/** Unit-type upgrades and rank-ups across every own unit, wherever it stands. */
function unitEntries(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  real: SimState,
  cityNumbers: ReadonlyMap<string, number>,
  names?: Names
): UpgradeEntry[] {
  const { rules } = ctx;
  const owners = territoryOwners(state, rules);
  const types = new Set(
    Object.values(state.units)
      .filter((u) => u.owner === playerId)
      .map((u) => u.type)
  );
  const entries: UpgradeEntry[] = [];
  for (const type of types) {
    for (const option of typePromotions(state, ctx, playerId, type, real, owners)) {
      const e = promotionEntry(
        state,
        ctx,
        playerId,
        real,
        cityNumbers,
        type,
        option,
        null,
        promoteCommands(option),
        names
      );
      if (e) entries.push(e);
    }
    const train = rules.units[type]?.military
      ? typeTrainOption(state, rules, playerId, type)
      : null;
    if (train) entries.push(rankEntry(rules, type, train, null));
  }
  return entries;
}

// ── Public ───────────────────────────────────────────────────────────────

/** Ready ones first, then by kind (cities, buildings, units, ranks), then as built. */
function sorted(entries: UpgradeEntry[]): UpgradeEntry[] {
  return entries
    .map((e, i) => ({ e, i }))
    .sort(
      (a, b) =>
        Number(b.e.canDo) - Number(a.e.canDo) ||
        KIND_ORDER.indexOf(a.e.kind) - KIND_ORDER.indexOf(b.e.kind) ||
        a.i - b.i
    )
    .map(({ e }) => e);
}

/** Every upgrade the player owns, ready ones first. */
export function ownUpgrades(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  real: SimState = state,
  cityNumbers: ReadonlyMap<string, number> = new Map(),
  names?: Names
): UpgradeEntry[] {
  return sorted([
    ...levelEntries(state, ctx, playerId, cityNumbers, names),
    ...unitEntries(state, ctx, playerId, real, cityNumbers, names),
  ]);
}

export interface CityUpgrades {
  /** The city's own next level (null at max). */
  city: UpgradeEntry | null;
  /** Levels of the buildings in its territory. */
  buildings: UpgradeEntry[];
  /** Unit upgrades and rank-ups usable here (units standing in its territory). */
  units: UpgradeEntry[];
}

/** One city's slice: its level, its buildings' levels, and the units that can upgrade there. */
export function cityUpgrades(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  cityId: string,
  real: SimState = state,
  cityNumbers: ReadonlyMap<string, number> = new Map(),
  names?: Names
): CityUpgrades {
  const { rules } = ctx;
  const levels = sorted(levelEntries(state, ctx, playerId, cityNumbers, names)).filter(
    (e) => e.cityId === cityId
  );
  const units: UpgradeEntry[] = [];
  for (const path of cityPromotions(state, ctx, playerId, cityId, real)) {
    const e = promotionEntry(
      state,
      ctx,
      playerId,
      real,
      cityNumbers,
      path.from,
      path.option,
      cityId,
      [cityPromoteCommand(cityId, path, true)],
      names
    );
    if (e) units.push(e);
  }
  const owners = territoryOwners(state, rules);
  const here = Object.values(state.units).filter(
    (u: Unit) =>
      u.owner === playerId &&
      owners.get(getHexTileMapKey(u.pos)) === cityId &&
      rules.units[u.type]?.military
  );
  for (const type of new Set(here.map((u) => u.type))) {
    const train = trainOption(
      state,
      rules,
      playerId,
      here.filter((u) => u.type === type)
    );
    if (train) units.push(rankEntry(rules, type, train, cityId));
  }
  return {
    city: levels.find((e) => e.kind === 'city') ?? null,
    buildings: levels.filter((e) => e.kind === 'building'),
    units: sorted(units),
  };
}

/** Counts per kind and the ready total, for the filter chips and the pill. */
export function upgradeCounts(entries: readonly UpgradeEntry[]) {
  const byKind: Record<UpgradeKind, number> = { city: 0, building: 0, unit: 0, rank: 0 };
  let ready = 0;
  for (const e of entries) {
    byKind[e.kind]++;
    if (e.canDo) ready++;
  }
  return { all: entries.length, byKind, ready };
}
